import EmptyStateWrapper from '@components/v2/ui/EmptyState/EmptyStateWrapper';
import { Box, CircularProgress } from '@mui/material';
import { type Ref } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Components,
  TableComponents,
  TableVirtuoso,
  TableVirtuosoHandle,
  Virtuoso,
  VirtuosoHandle,
} from 'react-virtuoso';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import useWidth from '../../../Hooks/useWidth';
import HideColumnsV2Wrapper from '../../../List/HideColumnsV2/components/HideColumnsV2Wrapper/HideColumnsV2Wrapper';
import SavableHideableColumnsVisibility from '../../../SavableHideableColumnsVisibility/SavableHideableColumnsVisibility';
import { useContentFrameScrollParent } from '../../../v2/layout/AppTemplate/hooks/useContentFrameScrollParent';
import ItemMenu from './ItemMenu';
import VirtualizedCardTable from './VirtualizedCardTable';
import VirtualizedItemTable from './VirtualizedItemTable';
import { BaseItem, InternalData, MenuType, VirtualizedTableProps } from './VirtualizedTable.types';
import VirtualizedTableHeaderActionButton from './VirtualizedTableHeaderActionButton';
import VirtualizedTableFooter from './footer/VirtualizedTableFooter';
import FakeHeader from './header/FakeHeader';
import VirtualizedTableHeader from './header/VirtualizedTableHeader';
import useStickyGroupHeaders from './hooks/useStickyGroupHeaders';
import useVirtualizedTable from './hooks/useVirtualizedTable';
import { useVirtuosoSnapshot } from './hooks/useVirtuosoSnapshot';
import { mobileComponents } from './mobileVirtualizedTable.components';
import { TableScrollerProvider } from './scroller/useTableScroller';
import { components } from './virtualizedTable.components';
import { useStyles } from './virtualizedTable.styles';
import { VIRTUALIZED_TABLE_CONTAINER } from './virtualizedTableIds';

const INITIAL_RENDERED_ROWS = 12;

const VirtualizedTable = <T extends BaseItem>({
  columns,
  columnsPreferenceRecognizedList,
  data: externalData,
  endReached,
  getRowActions,
  headerAction,
  renderParent,
  ExtraHeader,
  isFirstLoading,
  isLoadingNextPage,
  checkboxProps,
  renderTopItemList,
  useParentTableCell,
  disablePointerCursor,
  getIsDisabledRowAction,
  useCardView = false,
  fixedTopOffset,
  customScrollParent,
  restoreScrollOnMount = false,
  hideColumnsButton = false,
  emptyState,
  hasActiveFilters,
  stickyGroupHeaders = false,
  isFirstLevelTreeHidden = false,
}: VirtualizedTableProps<T>) => {
  const { translate } = useTranslation();
  const { mobileWidth } = useWidth();
  const { classes } = useStyles();
  const discoveredScrollParent = useContentFrameScrollParent();
  const effectiveScrollParent = customScrollParent ?? discoveredScrollParent;
  const useWindowScroll = !effectiveScrollParent;
  const { virtuosoRef, restoreStateFrom } = useVirtuosoSnapshot({
    enableScrollRestore: restoreScrollOnMount,
  });
  const { pathname, search } = useLocation();
  const hrefState = restoreScrollOnMount ? { originUrl: pathname, filters: search } : undefined;

  const isAllSelected = checkboxProps?.isAllSelected;
  const onToggleSelectAll = checkboxProps?.onToggleSelectAll;
  const disableCheckBox = checkboxProps?.disableCheckBox;
  const onToggleSelectRow = checkboxProps?.onToggleSelectRow;
  const selectedRows = checkboxProps?.selectedRows;
  const onToggleSelectParent = checkboxProps?.onToggleSelectParent;
  const canHideAnyColumns = columns.some(
    (column) => column.canHide === undefined || column.canHide,
  );
  const hasHideColumnsButton = canHideAnyColumns && !hideColumnsButton;
  const hasActionsColumn = !!getRowActions || hasHideColumnsButton;
  const isCardView = mobileWidth && useCardView;

  const {
    actions,
    availableColumns,
    canRestoreToDefaultColumns,
    childItemsByParentId,
    columnsToShow,
    data,
    emptyStateToRender,
    menu,
    onChangeMenu,
    onClickAction,
  } = useVirtualizedTable({
    columns,
    columnsPreferenceRecognizedList,
    disablePointerCursor,
    externalData,
    emptyState,
    hasActiveFilters,
    getRowActions,
    onToggleSelectParent,
    isAllSelected,
    isFirstLoading,
    selectedRows,
  });

  const { stickyGroup, stickyGroupOverlayRef, virtuosoContext } = useStickyGroupHeaders<T>({
    data,
    isEnabled: stickyGroupHeaders,
    isCardView,
  });

  const getToggleSelectParentHandler = (item: InternalData<T>) =>
    onToggleSelectParent && item.parentCheckboxState
      ? () => onToggleSelectParent(item, childItemsByParentId.get(item.id) ?? [])
      : undefined;

  const handleClickHideColumn = hasHideColumnsButton
    ? (e: React.MouseEvent<HTMLButtonElement>) => {
        onChangeMenu({ anchorEl: e.currentTarget, type: MenuType.HIDE_COLUMNS });
      }
    : undefined;

  if (emptyStateToRender) {
    return <EmptyStateWrapper>{emptyStateToRender}</EmptyStateWrapper>;
  }

  return (
    <TableScrollerProvider remeasureKey={columnsToShow.map((column) => column.name).join(',')}>
      {hasHideColumnsButton && columnsPreferenceRecognizedList && (
        <HideColumnsV2Wrapper
          isOpen={menu.type === MenuType.HIDE_COLUMNS && !!menu.anchorEl}
          onClose={() => onChangeMenu({ ...menu, anchorEl: null })}
          anchorEl={menu.anchorEl}
        >
          <SavableHideableColumnsVisibility
            list={columnsPreferenceRecognizedList}
            onClose={() => onChangeMenu({ ...menu, anchorEl: null })}
            columns={availableColumns.map((column) => ({
              isVisible: columnsToShow.map((c) => c.name)?.includes(column.name),
              key: column.name,
              name: column.title ? translate(column.title as LangEnum) : '',
              canHide: column.canHide,
              showByDefault: column.showByDefault,
              toggleVisibility: () => undefined,
            }))}
            canRestoreToDefaultColumns={canRestoreToDefaultColumns}
          />
        </HideColumnsV2Wrapper>
      )}
      <ItemMenu
        actions={actions}
        anchorEl={menu.anchorEl}
        onClose={() => onChangeMenu({ ...menu, anchorEl: null })}
        isOpen={menu.type === MenuType.ITEM && !!menu.anchorEl}
      />
      {isCardView ? (
        <>
          {headerAction && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 1 }}>
              <VirtualizedTableHeaderActionButton action={headerAction} />
            </Box>
          )}
          <Virtuoso<InternalData<T>>
            ref={virtuosoRef as Ref<VirtuosoHandle>}
            restoreStateFrom={restoreStateFrom}
            useWindowScroll={useWindowScroll}
            customScrollParent={effectiveScrollParent ?? undefined}
            initialItemCount={
              restoreStateFrom ? undefined : Math.min(data.length, INITIAL_RENDERED_ROWS)
            }
            increaseViewportBy={{ top: 500, bottom: 500 }}
            data={data.filter((item) => !item.isParent)}
            endReached={endReached}
            computeItemKey={(_index, item) => item.id}
            components={mobileComponents as Components<InternalData<T>>}
            itemContent={(index, item) => {
              const nextRow = data[index + 1];

              return (
                <VirtualizedCardTable<T>
                  item={item}
                  itemIndex={index}
                  isFirstLoading={isFirstLoading}
                  isLastChild={!!item.parentId && (!nextRow || item.parentId !== nextRow.parentId)}
                  columns={columnsToShow}
                  renderParent={renderParent}
                  onClickAction={!!getRowActions ? onClickAction : undefined}
                  useParentTableCell={useParentTableCell}
                  hasHideColumnsButton={hasHideColumnsButton}
                  hrefState={hrefState}
                  selectionProps={{
                    disableCheckBox: !!disableCheckBox,
                    onToggleSelectRow,
                  }}
                />
              );
            }}
          />
          {isLoadingNextPage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 3,
              }}
            >
              <CircularProgress size={16} />
            </Box>
          )}
        </>
      ) : (
        <Box
          className={classes.scrollerWrapper}
          sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}
        >
          <VirtualizedTableHeader
            ExtraHeader={!mobileWidth && ExtraHeader}
            hasRowActions={!!getRowActions}
            headerAction={headerAction}
            hasRowCheckbox={!!onToggleSelectRow}
            columns={columnsToShow}
            isAllSelected={!!isAllSelected && data.length > 0}
            onToggleSelectAll={onToggleSelectAll}
            onClickHideColumn={handleClickHideColumn}
            disableCheckBox={!!disableCheckBox}
            renderTopItemList={renderTopItemList}
            fixedTopOffset={fixedTopOffset}
            stickyGroup={stickyGroup}
            stickyGroupOverlayRef={stickyGroupOverlayRef}
          />
          <TableVirtuoso<InternalData<T>>
            ref={virtuosoRef as Ref<TableVirtuosoHandle>}
            restoreStateFrom={restoreStateFrom}
            id={VIRTUALIZED_TABLE_CONTAINER}
            useWindowScroll={useWindowScroll}
            customScrollParent={effectiveScrollParent ?? undefined}
            initialItemCount={
              restoreStateFrom ? undefined : Math.min(data.length, INITIAL_RENDERED_ROWS)
            }
            increaseViewportBy={{ top: 49, bottom: 147 }}
            data={data}
            endReached={endReached}
            computeItemKey={(_index, item) => item.id}
            context={virtuosoContext}
            components={components as TableComponents<InternalData<T>>}
            fixedFooterContent={() => {
              const actionsLength = [!!onToggleSelectAll, hasActionsColumn].filter(Boolean).length;
              const colSpan = actionsLength + columnsToShow.length;
              return (
                <VirtualizedTableFooter
                  isLoadingNextPage={!!isLoadingNextPage}
                  dataLength={data.length}
                  colSpan={colSpan}
                />
              );
            }}
            fixedHeaderContent={() => (
              <FakeHeader<T>
                hasExtraHeader={!mobileWidth && !!ExtraHeader}
                hasRowCheckbox={!!onToggleSelectRow}
                columns={columnsToShow}
                isUsingSelectAll={!!onToggleSelectAll}
                hasActionsColumn={hasActionsColumn}
              />
            )}
            itemContent={(index, item) => {
              const nextRow = data[index + 1];

              return (
                <VirtualizedItemTable<T>
                  item={item}
                  itemIndex={index}
                  isFirstLoading={isFirstLoading}
                  isLastChild={!!item.parentId && (!nextRow || item.parentId !== nextRow.parentId)}
                  columns={columnsToShow}
                  renderParent={renderParent}
                  onClickAction={!!getRowActions ? onClickAction : undefined}
                  disableActionColumn={getIsDisabledRowAction?.(item)}
                  useParentTableCell={useParentTableCell}
                  hasHideColumnsButton={hasHideColumnsButton}
                  isFirstLevelTreeHidden={isFirstLevelTreeHidden}
                  hrefState={hrefState}
                  selectionProps={{
                    disableCheckBox: !!disableCheckBox,
                    onToggleSelectRow,
                    parentCheckboxState: item.parentCheckboxState,
                    onToggleSelectParent: getToggleSelectParentHandler(item),
                  }}
                />
              );
            }}
          />
        </Box>
      )}
    </TableScrollerProvider>
  );
};

export default VirtualizedTable;
